import { doc, setDoc, getDoc, updateDoc, Timestamp } from 'firebase/firestore';
import { User } from 'firebase/auth';
import { db } from './firebase';
import { UserRegistrationData, UserProfile, CreateUserData } from '../types/user';

/**
 * Creates a new user document in Firestore
 */
export const createUserDocument = async (
  user: User,
  userData: CreateUserData
): Promise<void> => {
  try {
    // Wait for auth token to be ready
    await user.getIdToken();

    const userDoc: UserRegistrationData = {
      // Personal Information
      firstName: userData.firstName || '',
      lastName: userData.lastName || '',
      email: user.email || userData.email || '',
      displayName: user.displayName || userData.email?.split('@')[0] || 'User',

      // Contact Information
      country: userData.country || 'United States',
      streetAddress: userData.streetAddress || '',
      city: userData.city || '',
      region: userData.region || '',
      postalCode: userData.postalCode || '',

      // Account Settings
      userType: userData.userType || 'client',
      emailVerified: user.emailVerified || false,
      profileCompleted: false,
      themePreference: 1, // Default to dark mode

      // Professional fields (only for professionals)
      ...(userData.title !== undefined && { title: userData.title }),
      ...(userData.category !== undefined && { category: userData.category }),

      // Notification Preferences
      notifications: {
        bookings: userData.notifications?.bookings ?? true,
        ratings: userData.notifications?.ratings ?? true,
        payments: userData.notifications?.payments ?? true,
        cancellations: userData.notifications?.cancellations ?? true,
      },

      // Legal Acceptance (automatically tracked at registration)
      termsAcceptedAt: Timestamp.now(),
      termsVersion: '1.0',
      privacyPolicyAcceptedAt: Timestamp.now(),
      privacyPolicyVersion: '1.0',

      // Metadata
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    };

    // Use the Firebase Auth UID as the document ID
    const userRef = doc(db, 'users', user.uid);
    await setDoc(userRef, userDoc);
    
  } catch (error) {
    console.error('Error creating user document:', error);
    if (error instanceof Error) {
      throw new Error(`Failed to create user profile: ${error.message}`);
    }
    throw new Error('Failed to create user profile');
  }
};

/**
 * Gets a user document from Firestore
 */
export const getUserDocument = async (uid: string): Promise<UserProfile | null> => {
  try {
    const userRef = doc(db, 'users', uid);
    const userSnap = await getDoc(userRef);
    
    if (userSnap.exists()) {
      return {
        uid,
        ...userSnap.data()
      } as UserProfile;
    }
    
    return null;
  } catch (error) {
    if (error instanceof Error && error.message.includes('permissions')) {
      throw new Error(`Permission denied reading user ${uid}`);
    }
    console.error('Error getting user document:', error);
    if (error instanceof Error) {
      throw new Error(`Failed to get user profile: ${error.message}`);
    }
    throw new Error('Failed to get user profile');
  }
};

/**
 * Updates a user document in Firestore
 */
export const updateUserDocument = async (
  uid: string, 
  updates: Partial<UserRegistrationData>
): Promise<void> => {
  try {
    const userRef = doc(db, 'users', uid);
    await updateDoc(userRef, {
      ...updates,
      updatedAt: Timestamp.now(),
    });
    
  } catch (error) {
    console.error('Error updating user document:', error);
    if (error instanceof Error) {
      throw new Error(`Failed to update user profile: ${error.message}`);
    }
    throw new Error('Failed to update user profile');
  }
};

/**
 * Updates the email verification status for a user and populates default profile fields
 */
export const updateEmailVerificationStatus = async (
  uid: string,
  emailVerified: boolean
): Promise<void> => {
  try {
    const userRef = doc(db, 'users', uid);

    if (emailVerified) {
      // Get current user data to determine what defaults to populate
      const userSnap = await getDoc(userRef);
      if (userSnap.exists()) {
        const userData = userSnap.data() as UserRegistrationData;

        // Populate default fields based on user type
        const defaultFields = getDefaultProfileFields(userData.userType);

        await updateDoc(userRef, {
          emailVerified,
          ...defaultFields,
          updatedAt: Timestamp.now(),
        });
      } else {
        // Just update email verification if user document doesn't exist
        await updateDoc(userRef, {
          emailVerified,
          updatedAt: Timestamp.now(),
        });
      }
    } else {
      await updateDoc(userRef, {
        emailVerified,
        updatedAt: Timestamp.now(),
      });
    }

  } catch (error) {
    console.error('Error updating email verification status:', error);
    // Don't throw here - this is not critical
  }
};

/**
 * Gets default profile fields based on user type
 */
const getDefaultProfileFields = (userType: 'client' | 'professional') => {
  const commonDefaults = {
    firstName: 'First name',
    lastName: 'Last name',
    bio: userType === 'professional'
      ? 'Tell potential clients about your expertise, experience, and what makes you unique. This will help them understand how you can help with their needs.'
      : 'Tell professionals about yourself and what kind of help you\'re looking for. This will help them understand how to best assist you.',
    city: 'City',
    region: 'State/Province',
    streetAddress: 'Address',
    postalCode: 'Postal code',
  };

  return commonDefaults;
};

/**
 * Marks user profile as completed
 */
export const markProfileCompleted = async (uid: string): Promise<void> => {
  try {
    const userRef = doc(db, 'users', uid);
    await updateDoc(userRef, {
      profileCompleted: true,
      updatedAt: Timestamp.now(),
    });

  } catch (error) {
    console.error('Error marking profile as completed:', error);
    throw new Error('Failed to update profile completion status');
  }
};

/**
 * Checks if user profile needs completion
 */
export const checkProfileCompletionStatus = (userProfile: UserProfile): boolean => {
  // Check if essential fields are missing or have placeholder values
  const namePlaceholderValues = [
    'First name',
    'Last name'
  ];

  const hasNamePlaceholders =
    namePlaceholderValues.includes(userProfile.firstName) ||
    namePlaceholderValues.includes(userProfile.lastName);

  // Check if names are empty or just whitespace
  const hasEmptyNames =
    !userProfile.firstName?.trim() ||
    !userProfile.lastName?.trim();

  const hasDefaultBio =
    userProfile.bio?.includes('Tell potential clients about') ||
    userProfile.bio?.includes('Tell professionals about');

  // Profile needs completion if essential fields are missing, have placeholders, default bio text, or isn't marked complete
  return hasNamePlaceholders || hasEmptyNames || hasDefaultBio || !userProfile.profileCompleted;
};

/**
 * Checks if a user document exists in Firestore
 */
export const userDocumentExists = async (uid: string): Promise<boolean> => {
  try {
    const userRef = doc(db, 'users', uid);
    const userSnap = await getDoc(userRef);
    return userSnap.exists();
  } catch (error) {
    console.error('Error checking user document existence:', error);
    return false;
  }
};

/**
 * Updates the user's theme preference in Firestore
 * @param uid - User's Firebase UID
 * @param themePreference - Theme preference (0 = light, 1 = dark)
 */
export const updateThemePreference = async (
  uid: string,
  themePreference: number
): Promise<void> => {
  try {
    const userRef = doc(db, 'users', uid);
    await updateDoc(userRef, {
      themePreference,
      updatedAt: Timestamp.now(),
    });
  } catch (error) {
    console.error('Error updating theme preference:', error);
    // Don't throw - theme preference update is not critical
  }
};